// FILE: apps/admin/src/pages/Referrals.tsx
// VERSION: 2.1.0
// START_MODULE_CONTRACT
//   PURPOSE: Multi-generation (1..10) Referral Network & Configurable Promo/Partner Codes Control Center with dynamic generation settings, custom 1st-order discount & 1st-deposit bonus codes CRUD, top inviters, pairs, accruals ledger, and N-generation Upline/Downline tree explorer.
//   SCOPE: Referrals component.
//   DEPENDS: ../api.js, ../components/Icons.js, ../components/UserAvatar.js, ../toast.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   Referrals - Multi-generation Referral Program, Configurable Referral/Promo Codes CRUD, and N-level Tree Explorer
// END_MODULE_MAP

import { type AdminReferralCodeDto } from '@brainrotstore/api-contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { api, dt, num, rub } from '../api.js';
import { AdminIcon } from '../components/Icons.js';
import { resolveAdminUserDisplayName, UserAvatar } from '../components/UserAvatar.js';
import { useToast } from '../toast.js';

interface ReferralOverviewResponse {
  config: {
    inviterBonusXp: number;
    inviteeDiscountPercent: number;
    commissionPercentL1: number;
    commissionPercentL2: number;
    generationsCount?: number;
    generationPercents?: number[];
  };
  kpis: {
    totalPairsCount: number;
    qualifiedPairsCount: number;
    totalBonusXpAwarded: number;
    totalCommissionKopecks: number;
  };
  topInviters: Array<{
    userId: string;
    telegramId: number;
    username: string | null;
    firstName: string | null;
    customName?: string | null;
    photoUrl?: string | null;
    refCode?: string | null;
    invitedCount: number;
    qualifiedCount: number;
    earnedKopecks: number;
    earnedXp: number;
  }>;
  recentPairs: Array<{
    id: string;
    inviterUserId: string;
    inviterTelegramId?: number | null;
    inviterUsername?: string | null;
    inviterDisplayName?: string | null;
    inviterPhotoUrl?: string | null;
    inviteeUserId: string;
    inviteeTelegramId?: number | null;
    inviteeUsername?: string | null;
    inviteeDisplayName?: string | null;
    inviteePhotoUrl?: string | null;
    qualified: boolean;
    rewardedKopecks: number;
    createdAt: string;
  }>;
  recentAccruals: Array<{
    id: string;
    inviterUserId: string;
    inviterTelegramId?: number | null;
    inviterUsername?: string | null;
    inviterDisplayName?: string | null;
    inviteeUserId: string;
    inviteeTelegramId?: number | null;
    inviteeUsername?: string | null;
    inviteeDisplayName?: string | null;
    orderId: string | null;
    level: number;
    amountKopecks: number;
    xpAwarded: number;
    createdAt: string;
  }>;
}

interface TreeNodeDto {
  userId: string;
  telegramId: number;
  username: string | null;
  firstName: string | null;
  customName?: string | null;
  photoUrl?: string | null;
  isVerified?: boolean;
  level: number;
  qualified: boolean;
  earnedForRootKopecks: number;
  createdAt: string;
}

interface ReferralTreeResponse {
  rootUser: {
    userId: string;
    telegramId: number;
    username: string | null;
    firstName: string | null;
    customName?: string | null;
    photoUrl?: string | null;
    isVerified?: boolean;
    referralCode: string;
  };
  upline: TreeNodeDto | null;
  downlineL1: TreeNodeDto[];
  downlineL2: TreeNodeDto[];
  generations?: Array<{
    level: number;
    percent: number;
    nodes: TreeNodeDto[];
  }>;
}

type RefTab = 'overview' | 'codes' | 'top' | 'pairs' | 'accruals' | 'tree';

const DEFAULT_FALLBACK_PERCENTS = [5, 1.5, 1, 0.5, 0.5, 0.25, 0.25, 0.2, 0.15, 0.1];

// START_CONTRACT: Referrals
//   PURPOSE: Render the 6-tab Multi-Generation Referral Network, Configurable Referral/Promo Codes CRUD, and N-level Tree Explorer.
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: Calls GET /admin/referrals, PUT /admin/referrals/settings, GET/POST/PUT/DELETE /admin/referrals/codes, and GET /admin/referrals/tree/:userId
//   LINKS: M-ADMIN
// END_CONTRACT: Referrals
export function Referrals() {
  // START_BLOCK_REFERRALS
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [tab, setTab] = useState<RefTab>('overview');

  const [bonusXp, setBonusXp] = useState('200');
  const [discountPct, setDiscountPct] = useState('2');
  const [genCount, setGenCount] = useState<number>(2);
  const [genPercents, setGenPercents] = useState<string[]>(['5', '1.5']);

  // Configurable referral/promo code form state
  const codeEditorCardRef = useRef<HTMLDivElement | null>(null);
  const codeOrderDiscountInputRef = useRef<HTMLInputElement | null>(null);
  const [editingCodeId, setEditingCodeId] = useState<string | null>(null);
  const [editingOriginalCode, setEditingOriginalCode] = useState<string | null>(null);
  const [codeInput, setCodeInput] = useState('');
  const [ownerQueryInput, setOwnerQueryInput] = useState('');
  const [titleInput, setTitleInput] = useState('');
  const [codeOrderDiscountInput, setCodeOrderDiscountInput] = useState('5');
  const [codeDepositBonusInput, setCodeDepositBonusInput] = useState('10');
  const [maxUsesInput, setMaxUsesInput] = useState('');
  const [expiresAtInput, setExpiresAtInput] = useState('');
  const [codeActiveInput, setCodeActiveInput] = useState(true);
  const [codesSearch, setCodesSearch] = useState('');
  const [codesKindFilter, setCodesKindFilter] = useState<'all' | 'promo' | 'personal'>('all');

  const [treeQuery, setTreeQuery] = useState('');
  const [activeTreeUserId, setActiveTreeUserId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-referrals'],
    queryFn: () => api.get<ReferralOverviewResponse>('/admin/referrals'),
  });

  const { data: codesData } = useQuery({
    queryKey: ['admin-referral-codes'],
    queryFn: () => api.get<{ items: AdminReferralCodeDto[] }>('/admin/referrals/codes'),
  });

  const resetCodeForm = () => {
    setEditingCodeId(null);
    setEditingOriginalCode(null);
    setCodeInput('');
    setOwnerQueryInput('');
    setTitleInput('');
    setCodeOrderDiscountInput('5');
    setCodeDepositBonusInput('10');
    setMaxUsesInput('');
    setExpiresAtInput('');
    setCodeActiveInput(true);
  };

  const startEditCode = (c: AdminReferralCodeDto) => {
    setEditingCodeId(c.id);
    setEditingOriginalCode(c.code);
    setCodeInput(c.code);
    setOwnerQueryInput(
      c.ownerUsername ? `@${c.ownerUsername}` : c.ownerTelegramId ? String(c.ownerTelegramId) : (c.ownerUserId ?? ''),
    );
    setTitleInput(c.title ?? '');
    setCodeOrderDiscountInput(String(c.firstOrderDiscountPercent));
    setCodeDepositBonusInput(String(c.firstDepositBonusPercent));
    setMaxUsesInput(c.maxUses !== null ? String(c.maxUses) : '');
    setExpiresAtInput(c.expiresAt ? c.expiresAt.slice(0, 10) : '');
    setCodeActiveInput(c.active);
    setTab('codes');
    setTimeout(() => {
      codeEditorCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      codeOrderDiscountInputRef.current?.focus();
    }, 30);
  };

  const upsertCodeMut = useMutation({
    mutationFn: () => {
      const cleanCode = codeInput.trim().toUpperCase();
      if (cleanCode.length < 3) {
        throw new Error('Код должен содержать минимум 3 символа');
      }
      const orderPct = Number(codeOrderDiscountInput.replace(',', '.').trim() || '0');
      const depPct = Number(codeDepositBonusInput.replace(',', '.').trim() || '0');
      if (!Number.isFinite(orderPct) || orderPct < 0 || orderPct > 50) {
        throw new Error('Скидка на 1-й заказ должна быть от 0% до 50%');
      }
      if (!Number.isFinite(depPct) || depPct < 0 || depPct > 100) {
        throw new Error('Бонус к 1-му пополнению должен быть от 0% до 100%');
      }
      const maxUsesNum = maxUsesInput.trim() ? Number(maxUsesInput.trim()) : null;
      if (maxUsesNum !== null && (!Number.isInteger(maxUsesNum) || maxUsesNum < 1)) {
        throw new Error('Лимит активаций должен быть целым числом больше 0');
      }
      const payload = {
        code: cleanCode,
        ownerQuery: ownerQueryInput.trim() || null,
        title: titleInput.trim() || null,
        firstOrderDiscountPercent: orderPct,
        firstDepositBonusPercent: depPct,
        maxUses: maxUsesNum,
        expiresAt: expiresAtInput.trim()
          ? new Date(`${expiresAtInput.trim()}T23:59:59.000Z`).toISOString()
          : null,
        active: codeActiveInput,
      };
      return editingCodeId
        ? api.put<AdminReferralCodeDto>(
            `/admin/referrals/codes/${encodeURIComponent(editingCodeId)}`,
            payload,
          )
        : api.post<AdminReferralCodeDto>('/admin/referrals/codes', payload);
    },
    onSuccess: () => {
      toast(editingCodeId ? 'Код и бонусы обновлены' : 'Промокод создан');
      resetCodeForm();
      void qc.invalidateQueries({ queryKey: ['admin-referral-codes'] });
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: ['user-detail'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Ошибка сохранения промокода'),
  });

  const toggleCodeActiveMut = useMutation({
    mutationFn: (c: AdminReferralCodeDto) =>
      api.put<AdminReferralCodeDto>(`/admin/referrals/codes/${encodeURIComponent(c.id)}`, {
        code: c.code,
        ownerUserId: c.ownerUserId,
        title: c.title,
        firstOrderDiscountPercent: c.firstOrderDiscountPercent,
        firstDepositBonusPercent: c.firstDepositBonusPercent,
        maxUses: c.maxUses,
        expiresAt: c.expiresAt,
        active: !c.active,
      }),
    onSuccess: (updated) => {
      toast(updated.active ? `Код ${updated.code} включён` : `Код ${updated.code} отключён`);
      void qc.invalidateQueries({ queryKey: ['admin-referral-codes'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Ошибка изменения статуса кода'),
  });

  const deleteCodeMut = useMutation({
    mutationFn: (id: string) =>
      api.delete<{ deleted: boolean }>(`/admin/referrals/codes/${encodeURIComponent(id)}`),
    onSuccess: () => {
      toast('Код удалён');
      if (editingCodeId) resetCodeForm();
      void qc.invalidateQueries({ queryKey: ['admin-referral-codes'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Ошибка удаления кода'),
  });

  const copyCodeText = (value: string, label: string) => {
    void navigator.clipboard?.writeText(value);
    toast(`${label} скопирован`);
  };

  useEffect(() => {
    if (!data) return;
    setBonusXp(String(data.config.inviterBonusXp));
    setDiscountPct(String(data.config.inviteeDiscountPercent));
    const rawArr =
      Array.isArray(data.config.generationPercents) && data.config.generationPercents.length > 0
        ? data.config.generationPercents
        : [data.config.commissionPercentL1, data.config.commissionPercentL2];
    const count = Math.min(
      10,
      Math.max(1, Number(data.config.generationsCount ?? rawArr.length) || 2),
    );
    setGenCount(count);
    setGenPercents(
      Array.from({ length: count }, (_, idx) =>
        String(rawArr[idx] ?? DEFAULT_FALLBACK_PERCENTS[idx] ?? 0),
      ),
    );
  }, [data]);

  const changeGenerationsCount = (nextCount: number) => {
    const clamped = Math.min(10, Math.max(1, Math.round(nextCount)));
    setGenCount(clamped);
    setGenPercents((prev) =>
      Array.from({ length: clamped }, (_, idx) =>
        prev[idx] !== undefined ? prev[idx]! : String(DEFAULT_FALLBACK_PERCENTS[idx] ?? 0.5),
      ),
    );
  };

  const updateGenPercentAt = (idx: number, raw: string) => {
    setGenPercents((prev) => prev.map((v, i) => (i === idx ? raw : v)));
  };

  const { data: treeData, isFetching: treeLoading } = useQuery({
    queryKey: ['admin-referral-tree', activeTreeUserId],
    queryFn: () =>
      api.get<ReferralTreeResponse>(
        `/admin/referrals/tree/${encodeURIComponent(activeTreeUserId ?? '')}`,
      ),
    enabled: Boolean(activeTreeUserId),
  });

  const parsedPercents = genPercents
    .slice(0, genCount)
    .map((v) => Math.min(50, Math.max(0, Number(String(v).replace(',', '.')) || 0)));
  const totalChainPct = parsedPercents.reduce((s, n) => s + n, 0);

  const saveConfigMut = useMutation({
    mutationFn: () =>
      api.put('/admin/referrals/settings', {
        inviterBonusXp: Number(bonusXp) || 0,
        inviteeDiscountPercent: Number(String(discountPct).replace(',', '.')) || 0,
        generationsCount: genCount,
        generationPercents: parsedPercents,
        commissionPercentL1: parsedPercents[0] ?? 0,
        commissionPercentL2: parsedPercents[1] ?? 0,
      }),
    onSuccess: () => {
      toast('Настройки реферальной сети сохранены');
      void qc.invalidateQueries({ queryKey: ['admin-referrals'] });
      void qc.invalidateQueries({ queryKey: ['admin-referral-tree'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Ошибка сохранения'),
  });

  if (isLoading || !data) return <div className="muted">Загрузка реферальной сети…</div>;

  const activeConfigPercents =
    Array.isArray(data.config.generationPercents) && data.config.generationPercents.length > 0
      ? data.config.generationPercents
      : [data.config.commissionPercentL1, data.config.commissionPercentL2];

  const treeGenerations =
    treeData?.generations && treeData.generations.length > 0
      ? treeData.generations
      : treeData
        ? [
            {
              level: 1,
              percent: activeConfigPercents[0] ?? 0,
              nodes: treeData.downlineL1,
            },
            {
              level: 2,
              percent: activeConfigPercents[1] ?? 0,
              nodes: treeData.downlineL2,
            },
          ]
        : [];

  const codesList = codesData?.items ?? [];
  const promoCodesCount = codesList.filter((c) => !c.id.startsWith('user:')).length;
  const personalCodesCount = codesList.filter((c) =>
    Boolean(c.isUserDefaultCode || c.id.startsWith('user:')),
  ).length;
  const normalizedCodesQuery = codesSearch.trim().toLowerCase().replace(/^@/, '');
  const filteredCodesList = codesList.filter((c) => {
    const isPersonal = Boolean(c.isUserDefaultCode || c.id.startsWith('user:'));
    if (codesKindFilter === 'promo' && c.id.startsWith('user:')) return false;
    if (codesKindFilter === 'personal' && !isPersonal) return false;
    if (!normalizedCodesQuery) return true;
    const haystack = [
      c.code,
      c.ownerUsername ?? '',
      c.ownerDisplayName ?? '',
      c.ownerTelegramId ? String(c.ownerTelegramId) : '',
      c.title ?? '',
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(normalizedCodesQuery);
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Реферальная сеть и промокоды</h1>
          <div className="page-subtitle">
            Настройка поколений (1–10), настраиваемые реферальные и промо-коды со скидкой на 1-й заказ и бонусом к 1-му пополнению, аналитика рефоводов и дерево связей
          </div>
        </div>
      </div>

      {/* 4 KPI Cards */}
      <div className="grid metrics" style={{ marginBottom: 20 }}>
        <div className="kpi-card blue">
          <div className="kpi-label">Всего реферальных пар</div>
          <div className="kpi-value">{num(data.kpis.totalPairsCount)}</div>
          <div className="kpi-sub">Квалифицировано: {num(data.kpis.qualifiedPairsCount)}</div>
        </div>
        <div className="kpi-card green">
          <div className="kpi-label">Выплачено комиссий</div>
          <div className="kpi-value">{rub(data.kpis.totalCommissionKopecks)} ₽</div>
          <div className="kpi-sub">
            Активно поколений: {activeConfigPercents.length} ({activeConfigPercents.map((p) => `${p}%`).join(' / ')})
          </div>
        </div>
        <div className="kpi-card purple">
          <div className="kpi-label">Начислено бонусного XP</div>
          <div className="kpi-value">{num(data.kpis.totalBonusXpAwarded)} XP</div>
          <div className="kpi-sub">Бонус за друга: +{data.config.inviterBonusXp} XP</div>
        </div>
        <div className="kpi-card amber">
          <div className="kpi-label">Базовая скидка 1-го заказа</div>
          <div className="kpi-value">{data.config.inviteeDiscountPercent}%</div>
          <div className="kpi-sub">Настраиваемых кодов: {codesList.length}</div>
        </div>
      </div>

      {/* 6 Tabs */}
      <div className="tabs">
        <button
          type="button"
          className={`tab-btn ${tab === 'overview' ? 'active' : ''}`}
          onClick={() => setTab('overview')}
        >
          Поколения и ставки
        </button>
        <button
          type="button"
          className={`tab-btn ${tab === 'codes' ? 'active' : ''}`}
          onClick={() => setTab('codes')}
        >
          Настраиваемые коды ({codesList.length})
        </button>
        <button
          type="button"
          className={`tab-btn ${tab === 'top' ? 'active' : ''}`}
          onClick={() => setTab('top')}
        >
          Топ рефоводов ({data.topInviters.length})
        </button>
        <button
          type="button"
          className={`tab-btn ${tab === 'pairs' ? 'active' : ''}`}
          onClick={() => setTab('pairs')}
        >
          Реферальные пары ({data.recentPairs.length})
        </button>
        <button
          type="button"
          className={`tab-btn ${tab === 'accruals' ? 'active' : ''}`}
          onClick={() => setTab('accruals')}
        >
          Леджер начислений ({data.recentAccruals.length})
        </button>
        <button
          type="button"
          className={`tab-btn ${tab === 'tree' ? 'active' : ''}`}
          onClick={() => setTab('tree')}
        >
          Дерево поколений
        </button>
      </div>

      {tab === 'overview' && (
        <div className="card" style={{ maxWidth: 820 }}>
          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
            <div>
              <h2 style={{ margin: 0 }}>Настройка поколений реферальной сети</h2>
              <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>
                При каждой выполненной покупке вознаграждение начисляется вверх по цепочке пригласивших на заданное число поколений.
              </div>
            </div>
            <span className={`chip ${totalChainPct > 25 ? 'severity-WARN' : 'info'}`}>
              Суммарно по сети: {totalChainPct.toFixed(2)}%
            </span>
          </div>

          <div className="field">
            <div>
              <div style={{ fontWeight: 600 }}>Количество поколений (1–10)</div>
              <div className="muted" style={{ fontSize: 12 }}>
                Глубина партнёрского дерева начислений
              </div>
            </div>
            <div className="row" style={{ gap: 8 }}>
              <button
                type="button"
                className="btn ghost sm"
                disabled={genCount <= 1}
                onClick={() => changeGenerationsCount(genCount - 1)}
              >
                − Убрать
              </button>
              <select
                value={genCount}
                onChange={(e) => changeGenerationsCount(Number(e.target.value))}
                style={{ minWidth: 150 }}
              >
                {Array.from({ length: 10 }, (_, idx) => idx + 1).map((n) => (
                  <option key={n} value={n}>
                    {n} {n === 1 ? 'поколение' : n < 5 ? 'поколения' : 'поколений'}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn ghost sm"
                disabled={genCount >= 10}
                onClick={() => changeGenerationsCount(genCount + 1)}
              >
                + Поколение
              </button>
            </div>
          </div>

          <div className="gen-config-list">
            {Array.from({ length: genCount }, (_, idx) => {
              const level = idx + 1;
              const numVal = parsedPercents[idx] ?? 0;
              const sampleRub = ((1000 * numVal) / 100).toFixed(2);
              return (
                <div className="gen-config-row" key={level}>
                  <div>
                    <div style={{ fontWeight: 600 }}>Поколение {level}</div>
                    <div className="muted" style={{ fontSize: 11.5 }}>
                      {level === 1 ? 'Прямой пригласивший' : `${level}-е колено вверх`}
                    </div>
                  </div>
                  <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="50"
                      style={{ width: 92 }}
                      value={genPercents[idx] ?? '0'}
                      onChange={(e) => updateGenPercentAt(idx, e.target.value)}
                    />
                    <span className="muted">%</span>
                  </div>
                  <div className="muted" style={{ fontSize: 12.5 }}>
                    С заказа 1 000 ₽ → <strong style={{ color: 'var(--ink)' }}>{sampleRub} ₽</strong>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="field">
            <div>
              <div style={{ fontWeight: 600 }}>Бонус опыта (XP) за приглашение</div>
              <div className="muted" style={{ fontSize: 12 }}>
                Единоразовый бонус XP инвайтеру 1-го поколения
              </div>
            </div>
            <div className="row" style={{ gap: 8 }}>
              <input
                style={{ width: 130 }}
                value={bonusXp}
                onChange={(e) => setBonusXp(e.target.value)}
              />
              <span className="muted">XP</span>
            </div>
          </div>

          <div className="field" style={{ borderBottom: 'none' }}>
            <div>
              <div style={{ fontWeight: 600 }}>Скидка приглашённому другу на 1-й заказ (%)</div>
              <div className="muted" style={{ fontSize: 12 }}>
                Разовая скидка по реферальной ссылке — действует строго только на первый заказ друга
              </div>
            </div>
            <div className="row" style={{ gap: 8 }}>
              <input
                style={{ width: 130 }}
                value={discountPct}
                onChange={(e) => setDiscountPct(e.target.value)}
              />
              <span className="muted">%</span>
            </div>
          </div>

          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
            <button
              type="button"
              className="btn primary"
              disabled={saveConfigMut.isPending}
              onClick={() => saveConfigMut.mutate()}
            >
              <AdminIcon name="check" size={14} />
              <span>Сохранить настройки поколений</span>
            </button>
          </div>
        </div>
      )}

      {tab === 'codes' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div className="card" ref={codeEditorCardRef}>
            {editingCodeId && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '10px 14px',
                  marginBottom: 14,
                  borderRadius: 10,
                  background: 'rgba(130, 148, 255, 0.14)',
                  border: '1px solid rgba(130, 148, 255, 0.35)',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <AdminIcon name="sparkle" size={14} />
                  <strong style={{ color: '#c7d2fe' }}>
                    Редактирование существующего кода: {editingOriginalCode ?? codeInput}
                  </strong>
                  {editingCodeId.startsWith('user:') && (
                    <span className="muted" style={{ fontSize: 12 }}>
                      · личный код игрока (сохранение задаст % бонусов и обновит код в профиле)
                    </span>
                  )}
                </div>
                <button type="button" className="btn sm ghost" onClick={resetCodeForm}>
                  <AdminIcon name="close" size={12} />
                  <span>Отменить редактирование</span>
                </button>
              </div>
            )}

            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 14 }}>
              <div>
                <h2 style={{ margin: 0 }}>
                  {editingCodeId
                    ? `Редактирование существующего кода: ${editingOriginalCode ?? codeInput}`
                    : 'Создать реферальный или промо-код'}
                </h2>
                <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>
                  Настройте индивидуальный % скидки на первую покупку и/или % бонуса к первому пополнению (СБП / TON / Stars). Вы также можете выбрать любой существующий личный код игрока ниже и задать для него индивидуальные бонусы или изменить сам код.
                </div>
              </div>
            </div>

            <div className="grid cols-3" style={{ gap: 12 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>Код (3–24 символа)</span>
                <input
                  className="mono"
                  placeholder="Например: VIP2026 или BLOGGER15"
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>
                  Владелец-партнёр (необязательно)
                </span>
                <input
                  placeholder="@username, Telegram ID или пусто (промо)"
                  value={ownerQueryInput}
                  onChange={(e) => setOwnerQueryInput(e.target.value)}
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>Название / кампания</span>
                <input
                  placeholder="Интеграция YouTube / Весенняя акция"
                  value={titleInput}
                  onChange={(e) => setTitleInput(e.target.value)}
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>
                  Скидка на 1-ю покупку (0–50%)
                </span>
                <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                  <input
                    ref={codeOrderDiscountInputRef}
                    type="number"
                    min="0"
                    max="50"
                    step="0.5"
                    style={{ width: '100%' }}
                    value={codeOrderDiscountInput}
                    onChange={(e) => setCodeOrderDiscountInput(e.target.value)}
                  />
                  <span className="muted">%</span>
                </div>
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>
                  Бонус к 1-му пополнению (0–100%)
                </span>
                <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    style={{ width: '100%' }}
                    value={codeDepositBonusInput}
                    onChange={(e) => setCodeDepositBonusInput(e.target.value)}
                  />
                  <span className="muted">%</span>
                </div>
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>Лимит активаций</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  placeholder="Без лимита"
                  value={maxUsesInput}
                  onChange={(e) => setMaxUsesInput(e.target.value)}
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>Действует до (необязательно)</span>
                <input
                  type="date"
                  value={expiresAtInput}
                  onChange={(e) => setExpiresAtInput(e.target.value)}
                />
              </label>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  paddingTop: 22,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={codeActiveInput}
                  onChange={(e) => setCodeActiveInput(e.target.checked)}
                />
                <span style={{ fontWeight: 600, fontSize: 13 }}>Код активен для ввода</span>
              </label>
            </div>

            <div
              className="row"
              style={{ justifyContent: 'space-between', marginTop: 16, flexWrap: 'wrap', gap: 10 }}
            >
              <span className="muted mono" style={{ fontSize: 12, wordBreak: 'break-all' }}>
                {codeInput.trim()
                  ? `Ссылка: https://t.me/starsfish_game?startapp=${codeInput.trim().toUpperCase()}`
                  : 'Введите код, чтобы увидеть готовую ссылку для Mini App'}
              </span>
              <button
                type="button"
                className="btn primary"
                disabled={upsertCodeMut.isPending || codeInput.trim().length < 3}
                onClick={() => upsertCodeMut.mutate()}
              >
                <AdminIcon name="check" size={14} />
                <span>
                  {upsertCodeMut.isPending
                    ? 'Сохранение…'
                    : editingCodeId
                      ? 'Сохранить изменения кода'
                      : 'Создать настраиваемый код'}
                </span>
              </button>
            </div>
          </div>

          <div className="toolbar" style={{ marginBottom: 0 }}>
            <div className="tabs" style={{ marginBottom: 0 }}>
              <button
                type="button"
                className={`tab-btn ${codesKindFilter === 'all' ? 'active' : ''}`}
                onClick={() => setCodesKindFilter('all')}
              >
                Все коды ({codesList.length})
              </button>
              <button
                type="button"
                className={`tab-btn ${codesKindFilter === 'promo' ? 'active' : ''}`}
                onClick={() => setCodesKindFilter('promo')}
              >
                Промо и кастомные ({promoCodesCount})
              </button>
              <button
                type="button"
                className={`tab-btn ${codesKindFilter === 'personal' ? 'active' : ''}`}
                onClick={() => setCodesKindFilter('personal')}
              >
                Личные коды игроков ({personalCodesCount})
              </button>
            </div>
            <input
              style={{ flex: '1 1 220px', minWidth: 0 }}
              placeholder="Поиск по коду SF…, @username, нику или TG ID…"
              value={codesSearch}
              onChange={(e) => setCodesSearch(e.target.value)}
            />
          </div>

          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Код и кампания</th>
                    <th>Тип / Владелец</th>
                    <th>Скидка 1-й заказ</th>
                    <th>Бонус 1-й депозит</th>
                    <th>Активации</th>
                    <th>Статус и срок</th>
                    <th style={{ textAlign: 'right' }}>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCodesList.map((c) => {
                    const link = `https://t.me/starsfish_game?startapp=${c.code}`;
                    const isPersonalCode = Boolean(c.isUserDefaultCode || c.id.startsWith('user:'));
                    const isVirtualUserCode = c.id.startsWith('user:');
                    const isExpired = Boolean(
                      c.expiresAt && new Date(c.expiresAt).getTime() < Date.now(),
                    );
                    const isExhausted = Boolean(
                      c.maxUses !== null && c.usesCount >= c.maxUses,
                    );
                    const statusLabel = !c.active
                      ? 'Отключён'
                      : isExpired
                        ? 'Истёк'
                        : isExhausted
                          ? 'Лимит исчерпан'
                          : 'Активен';
                    const statusTone =
                      statusLabel === 'Активен' ? 'ok' : 'severity-WARN';

                    return (
                      <tr key={c.id}>
                        <td>
                          <div className="row" style={{ gap: 6 }}>
                            <span className="chip info mono" style={{ fontWeight: 700 }}>
                              {c.code}
                            </span>
                            <button
                              type="button"
                              className="btn sm ghost"
                              title="Скопировать ссылку"
                              onClick={() => copyCodeText(link, `Ссылка ${c.code}`)}
                            >
                              <AdminIcon name="copy" size={12} />
                            </button>
                          </div>
                          {c.title && (
                            <div className="muted" style={{ fontSize: 11.5, marginTop: 3 }}>
                              {c.title}
                            </div>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                            {isPersonalCode ? (
                              <span
                                className="chip"
                                style={{
                                  background: 'rgba(139, 92, 246, 0.15)',
                                  borderColor: 'rgba(167, 139, 250, 0.35)',
                                  color: '#c4b5fd',
                                  fontSize: 11,
                                }}
                              >
                                Личный код игрока
                              </span>
                            ) : (
                              <span className="chip info" style={{ fontSize: 11 }}>
                                Промокод
                              </span>
                            )}
                            {c.ownerUserId && (
                              <div>
                                <div
                                  style={{ fontWeight: 600, cursor: 'pointer', fontSize: 13 }}
                                  onClick={() =>
                                    navigate(`/users?userId=${encodeURIComponent(c.ownerUserId!)}`)
                                  }
                                >
                                  {c.ownerDisplayName ?? 'Партнёр'}
                                </div>
                                <div className="muted mono" style={{ fontSize: 11 }}>
                                  {c.ownerUsername
                                    ? `@${c.ownerUsername}`
                                    : c.ownerTelegramId
                                      ? `#${c.ownerTelegramId}`
                                      : c.ownerUserId.slice(0, 8)}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="mono">
                          {c.firstOrderDiscountPercent > 0 ? (
                            <strong style={{ color: 'var(--green)' }}>
                              −{c.firstOrderDiscountPercent}%
                            </strong>
                          ) : (
                            <span className="muted">0%</span>
                          )}
                        </td>
                        <td className="mono">
                          {c.firstDepositBonusPercent > 0 ? (
                            <strong style={{ color: 'var(--accent)' }}>
                              +{c.firstDepositBonusPercent}%
                            </strong>
                          ) : (
                            <span className="muted">0%</span>
                          )}
                        </td>
                        <td className="mono">
                          {num(c.usesCount)}
                          {c.maxUses !== null ? ` / ${num(c.maxUses)}` : ' / ∞'}
                        </td>
                        <td>
                          <span className={`chip ${statusTone}`}>{statusLabel}</span>
                          {c.expiresAt && (
                            <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>
                              до {dt(c.expiresAt)}
                            </div>
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              className="btn sm ghost"
                              onClick={() => startEditCode(c)}
                            >
                              Изменить % / код
                            </button>
                            <button
                              type="button"
                              className="btn sm ghost"
                              disabled={toggleCodeActiveMut.isPending}
                              onClick={() => toggleCodeActiveMut.mutate(c)}
                            >
                              {c.active ? 'Выкл' : 'Вкл'}
                            </button>
                            {!isVirtualUserCode && (
                              <button
                                type="button"
                                className="btn sm danger"
                                disabled={deleteCodeMut.isPending}
                                onClick={() => deleteCodeMut.mutate(c.id)}
                              >
                                Удалить
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {!filteredCodesList.length && (
                    <tr>
                      <td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                        {codesList.length > 0
                          ? 'По выбранному фильтру или поисковому запросу коды не найдены'
                          : 'Настраиваемых кодов пока нет — создайте первый промокод или партнёрский код в форме выше'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === 'top' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Пользователь</th>
                  <th>Реф-код</th>
                  <th>Приглашено</th>
                  <th>Купили (квалиф.)</th>
                  <th>Заработано (₽)</th>
                  <th>Заработано (XP)</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {data.topInviters.map((inv) => {
                  const dn = resolveAdminUserDisplayName(inv);
                  return (
                    <tr key={inv.userId}>
                      <td>
                        <div className="user-cell">
                          <UserAvatar photoUrl={inv.photoUrl} name={dn.primary} size={32} />
                          <div className="user-cell-meta">
                            <div className="user-cell-name-row">
                              <strong
                                style={{ cursor: 'pointer' }}
                                onClick={() => navigate(`/users?userId=${encodeURIComponent(inv.userId)}`)}
                              >
                                {dn.primary}
                              </strong>
                              {dn.originalTgName && (
                                <span className="tg-orig-tag">TG: {dn.originalTgName}</span>
                              )}
                            </div>
                            <div className="muted mono" style={{ fontSize: 11.5 }}>
                              {inv.username ? `@${inv.username}` : `#${inv.telegramId}`}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="chip info mono">{inv.refCode ?? '—'}</span>
                      </td>
                      <td className="mono">{num(inv.invitedCount)}</td>
                      <td className="mono">{num(inv.qualifiedCount)}</td>
                      <td className="mono">{rub(inv.earnedKopecks)} ₽</td>
                      <td className="mono">{num(inv.earnedXp)} XP</td>
                      <td>
                        <button
                          type="button"
                          className="btn sm ghost"
                          onClick={() => {
                            setActiveTreeUserId(inv.userId);
                            setTreeQuery(inv.userId);
                            setTab('tree');
                          }}
                        >
                          Дерево поколений
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {!data.topInviters.length && (
                  <tr>
                    <td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                      Данных по рефоводам пока нет
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'pairs' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Кто пригласил</th>
                  <th>Кого пригласил</th>
                  <th>Квалифицирован</th>
                  <th>Награда L1 (₽)</th>
                  <th>Дата</th>
                </tr>
              </thead>
              <tbody>
                {data.recentPairs.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <div className="user-cell">
                        <UserAvatar
                          photoUrl={p.inviterPhotoUrl}
                          name={p.inviterDisplayName ?? p.inviterUsername}
                          size={28}
                        />
                        <div>
                          <div style={{ fontWeight: 600 }}>
                            {p.inviterDisplayName ?? p.inviterUserId.slice(0, 10)}
                          </div>
                          <div className="muted mono" style={{ fontSize: 11 }}>
                            {p.inviterUsername
                              ? `@${p.inviterUsername}`
                              : p.inviterTelegramId
                                ? `#${p.inviterTelegramId}`
                                : p.inviterUserId.slice(0, 8)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="user-cell">
                        <UserAvatar
                          photoUrl={p.inviteePhotoUrl}
                          name={p.inviteeDisplayName ?? p.inviteeUsername}
                          size={28}
                        />
                        <div>
                          <div style={{ fontWeight: 600 }}>
                            {p.inviteeDisplayName ?? p.inviteeUserId.slice(0, 10)}
                          </div>
                          <div className="muted mono" style={{ fontSize: 11 }}>
                            {p.inviteeUsername
                              ? `@${p.inviteeUsername}`
                              : p.inviteeTelegramId
                                ? `#${p.inviteeTelegramId}`
                                : p.inviteeUserId.slice(0, 8)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`chip ${p.qualified ? 'ok' : 'AWAITING_PAYMENT'}`}>
                        {p.qualified ? 'Купил' : 'Регистрация'}
                      </span>
                    </td>
                    <td className="mono">{rub(p.rewardedKopecks)} ₽</td>
                    <td className="muted">{dt(p.createdAt)}</td>
                  </tr>
                ))}
                {!data.recentPairs.length && (
                  <tr>
                    <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                      Реферальных пар пока нет
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'accruals' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Получатель комиссии</th>
                  <th>Покупатель</th>
                  <th>Поколение</th>
                  <th>Сумма (₽)</th>
                  <th>XP</th>
                  <th>Дата</th>
                </tr>
              </thead>
              <tbody>
                {data.recentAccruals.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>
                        {a.inviterDisplayName ?? a.inviterUserId.slice(0, 10)}
                      </div>
                      <div className="muted mono" style={{ fontSize: 11 }}>
                        {a.inviterUsername ? `@${a.inviterUsername}` : a.inviterUserId.slice(0, 8)}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>
                        {a.inviteeDisplayName ?? a.inviteeUserId.slice(0, 10)}
                      </div>
                      <div className="muted mono" style={{ fontSize: 11 }}>
                        {a.inviteeUsername ? `@${a.inviteeUsername}` : a.inviteeUserId.slice(0, 8)}
                      </div>
                    </td>
                    <td>
                      <span className="chip info">Поколение {a.level}</span>
                    </td>
                    <td className="mono">+{rub(a.amountKopecks)} ₽</td>
                    <td className="mono">+{num(a.xpAwarded)} XP</td>
                    <td className="muted">{dt(a.createdAt)}</td>
                  </tr>
                ))}
                {!data.recentAccruals.length && (
                  <tr>
                    <td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                      Начислений пока нет
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'tree' && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Исследователь дерева поколений (Upline + 1..{genCount})</h2>
          <div className="toolbar">
            <input
              style={{ flex: '1 1 220px', minWidth: 0 }}
              placeholder="Введите UUID или Telegram ID пользователя…"
              value={treeQuery}
              onChange={(e) => setTreeQuery(e.target.value)}
            />
            <button
              type="button"
              className="btn primary"
              disabled={!treeQuery.trim() || treeLoading}
              onClick={() => setActiveTreeUserId(treeQuery.trim())}
            >
              Построить дерево связей
            </button>
          </div>

          {treeLoading && <div className="muted">Построение графа поколений…</div>}

          {treeData && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 14 }}>
              <div className="grid cols-2">
                <div className="user-section">
                  <div className="muted" style={{ fontSize: 11.5, marginBottom: 8 }}>
                    Выбранный узел
                  </div>
                  <div className="user-cell">
                    <UserAvatar
                      photoUrl={treeData.rootUser.photoUrl}
                      name={treeData.rootUser.customName ?? treeData.rootUser.firstName}
                      size={40}
                    />
                    <div>
                      <div className="user-cell-name-row">
                        <strong>
                          {treeData.rootUser.customName ?? treeData.rootUser.firstName ?? 'Пользователь'}
                        </strong>
                        {treeData.rootUser.isVerified && <AdminIcon name="verified" size={14} />}
                        <span className="chip info mono">{treeData.rootUser.referralCode}</span>
                      </div>
                      <div className="muted mono" style={{ fontSize: 12 }}>
                        {treeData.rootUser.username
                          ? `@${treeData.rootUser.username}`
                          : `#${treeData.rootUser.telegramId}`}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="user-section">
                  <div className="muted" style={{ fontSize: 11.5, marginBottom: 8 }}>
                    Кто пригласил (Upline)
                  </div>
                  {treeData.upline ? (
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <div className="user-cell">
                        <UserAvatar
                          photoUrl={treeData.upline.photoUrl}
                          name={treeData.upline.customName ?? treeData.upline.firstName}
                          size={36}
                        />
                        <div>
                          <div className="user-cell-name-row">
                            <strong>
                              {treeData.upline.customName ?? treeData.upline.firstName ?? '—'}
                            </strong>
                            {treeData.upline.isVerified && <AdminIcon name="verified" size={13} />}
                          </div>
                          <div className="muted mono" style={{ fontSize: 12 }}>
                            {treeData.upline.username
                              ? `@${treeData.upline.username}`
                              : `#${treeData.upline.telegramId}`}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn sm ghost"
                        onClick={() => {
                          setTreeQuery(treeData.upline!.userId);
                          setActiveTreeUserId(treeData.upline!.userId);
                        }}
                      >
                        Перейти выше
                      </button>
                    </div>
                  ) : (
                    <div className="muted">Органический пользователь (без инвайтера)</div>
                  )}
                </div>
              </div>

              <div className="grid cols-2">
                {treeGenerations.map((gen) => (
                  <div className="user-section" key={gen.level}>
                    <div className="user-section-head" style={{ marginBottom: 10 }}>
                      <div className="user-section-title">
                        Поколение {gen.level} ({gen.nodes.length})
                      </div>
                      <span className="chip info">Ставка {gen.percent}%</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {gen.nodes.map((u) => {
                        const dn = resolveAdminUserDisplayName(u);
                        return (
                          <div
                            key={u.userId}
                            className="row"
                            style={{
                              justifyContent: 'space-between',
                              paddingBottom: 8,
                              borderBottom: '1px solid var(--hair)',
                            }}
                          >
                            <div className="user-cell">
                              <UserAvatar photoUrl={u.photoUrl} name={dn.primary} size={30} />
                              <div>
                                <div className="user-cell-name-row">
                                  <span style={{ fontWeight: 600 }}>{dn.primary}</span>
                                  {u.isVerified && <AdminIcon name="verified" size={13} />}
                                  {u.qualified && <span className="chip ok">Купил</span>}
                                </div>
                                <div className="muted mono" style={{ fontSize: 11.5 }}>
                                  {u.username ? `@${u.username}` : `#${u.telegramId}`} · Принёс:{' '}
                                  {rub(u.earnedForRootKopecks)} ₽
                                </div>
                              </div>
                            </div>
                            <button
                              type="button"
                              className="btn sm ghost"
                              onClick={() => {
                                setTreeQuery(u.userId);
                                setActiveTreeUserId(u.userId);
                              }}
                            >
                              Фокус
                            </button>
                          </div>
                        );
                      })}
                      {!gen.nodes.length && (
                        <div className="muted" style={{ fontSize: 12.5 }}>
                          Нет рефералов в поколении {gen.level}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
  // END_BLOCK_REFERRALS
}
